'use client';

import React, { useEffect, useRef } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
Chart.register(...registerables);

interface AgriculturalChartProps {
  data: any;
}

export default function AgriculturalChart({ data }: AgriculturalChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || !data) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Extract hourly intervals
    const intervals = data.timelines?.[0]?.intervals || [];
    
    // Prepare chart data
    const chartData = {
      labels: intervals.map((interval: any) => 
        new Date(interval.time).toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      ),
      datasets: [
        {
          label: 'Temperature (°C)',
          data: intervals.map((interval: any) => interval.values.temperature),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Heat Stress Threshold (25°C)',
          data: Array(intervals.length).fill(25),
          borderColor: 'rgb(239, 68, 68)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'Humidity (%)',
          data: intervals.map((interval: any) => interval.values.humidity),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          yAxisID: 'y1',
          pointRadius: 3
        }
      ]
    };

    const config: ChartConfiguration = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Live Farm Weather Monitoring',
            font: {
              size: 16,
              weight: 'bold'
            }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                             afterBody: (tooltipItems: any[]) => {
                const index = tooltipItems[0].dataIndex;
                const interval = intervals[index];
                if (interval) {
                  return [
                    `Wind: ${interval.values.windSpeed} m/s`,
                    `Precipitation: ${interval.values.precipitationProbability}%`,
                    `Evapotranspiration: ${interval.values.evapotranspiration}`,
                    `Dew Point: ${interval.values.dewPoint}°C`
                  ];
                }
                return [];
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Time'
            }
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Temperature (°C)'
            },
            min: 10,
            max: 30
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Humidity (%)'
            },
            min: 0,
            max: 100,
            grid: {
              drawOnChartArea: false,
            },
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    };

    // Create chart
    chartInstance.current = new Chart(ctx, config);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  // Calculate current conditions
  const currentInterval = data?.timelines?.[0]?.intervals?.[0];
  const currentTemp = currentInterval?.values?.temperature;
  const currentHumidity = currentInterval?.values?.humidity;
  const evapotranspiration = currentInterval?.values?.evapotranspiration;

  // Determine irrigation recommendation
  const getIrrigationStatus = () => {
    if (!currentInterval) return { status: 'Unknown', color: 'gray', message: 'No data available' };
    
    if (currentHumidity < 40 && evapotranspiration > 0.3) {
      return { 
        status: 'Irrigation Needed', 
        color: 'red', 
        message: 'Low humidity & high evapotranspiration rate detected' 
      };
    } else if (currentHumidity > 80) {
      return { 
        status: 'Monitor for Fungal Risk', 
        color: 'yellow', 
        message: 'High humidity may promote fungal growth' 
      };
    } else {
      return { 
        status: 'Optimal Conditions', 
        color: 'green', 
        message: 'Moisture levels are within normal range' 
      };
    }
  };

  const irrigationStatus = getIrrigationStatus();

  return (
    <div className="space-y-6">
      {/* Chart Container */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div style={{ height: '400px' }}>
          <canvas ref={chartRef}></canvas>
        </div>
      </div>

      {/* Current Conditions Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Temperature */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-600 mb-1">Current Temperature</div>
          <div className="text-2xl font-bold text-gray-900">
            {currentTemp ? `${currentTemp}°C` : '—'}
          </div>
          <div className={`text-sm mt-1 ${
            currentTemp && currentTemp > 25 ? 'text-red-600' : 'text-green-600'
          }`}>
            {currentTemp && currentTemp > 25 ? '⚠️ Heat stress risk' : '✅ Optimal range'}
          </div>
        </div>

        {/* Humidity */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-600 mb-1">Humidity</div>
          <div className="text-2xl font-bold text-gray-900">
            {currentHumidity ? `${currentHumidity}%` : '—'}
          </div>
          <div className={`text-sm mt-1 ${
            currentHumidity && (currentHumidity < 40 || currentHumidity > 80) 
              ? 'text-yellow-600' : 'text-green-600'
          }`}>
            {currentHumidity && currentHumidity < 40 ? '📉 Low' : 
             currentHumidity && currentHumidity > 80 ? '📈 High' : '📊 Normal'}
          </div>
        </div>

        {/* Irrigation Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-600 mb-1">Irrigation Status</div>
          <div className={`text-lg font-bold ${
            irrigationStatus.color === 'red' ? 'text-red-600' :
            irrigationStatus.color === 'yellow' ? 'text-yellow-600' :
            irrigationStatus.color === 'green' ? 'text-green-600' : 'text-gray-600'
          }`}>
            {irrigationStatus.status}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {irrigationStatus.message}
          </div>
        </div>
      </div>

      {/* Data Source Info */}
      <div className="text-xs text-gray-500 text-center">
        Data from Tomorrow.io Weather API • Farm Location: {data?.location?.lat}, {data?.location?.lon}
      </div>
    </div>
  );
} 